<template>
  <v-card>
    <v-card-title class="primary white--text headline">
      <span>{{ $t('selectPayees') }}</span>
      <v-spacer/>
      <Icon
      @icon-clicked="$emit('close', { refresh: false })"
      :tooltipText="$t('close')"
      iconColor="white"
      icon="mdi-close"
      :small="false"
      large
      />
    </v-card-title>
    <v-card-text>
      <span v-if="loading" style="background-color: red;">
        <v-row class="mt-2">
          <v-col align="center">
            <v-progress-circular indeterminate color="primary"/>
          </v-col>
        </v-row>
      </span>
      <span v-else-if="noPayeesWithSettlementContacts">
        <v-row class="mt-1">
          <v-col align="center">
            <p class="subtitle-1 black--text font-weight-medium" style="white-space: pre-line">
              {{ $t('noAccountsWithSpecifiedSettlementRecipients') }}
            </p>
          </v-col>
        </v-row>
      </span>
      <v-row v-else>
        <v-col>
          <v-row>
            <v-col class="mb-n2" cols="9">
              <v-checkbox
              :label="$t('allPayees')"
              v-model="allPayees"
              @change="allPayeeCheckboxToggled"/>
            </v-col>
            <v-spacer/>
            <span class="mt-8 mr-1">
              <Icon
              v-if="deliveryMethod.value === BulkSettlementDeliveryMode.Email.value"
              :small="false"
              icon="mdi-information-outline"
              :tooltipText="$t('excludesAccountsWithoutSettlementRecipient')"/>
            </span>
          </v-row>
          <hr class="mb-n4 mt-n3"/>
          <v-row v-for="(payee, index) in payees" :key="index" class="mt-1">
            <v-col class="mb-n8">
              <v-checkbox
              :label="payee.name"
              v-model="payee.selected"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col align="right">
              <v-btn
              class="primary"
              @click="downloadMethod()"
              :disabled="buttonDisabled">
                {{ btnText }}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { SettlementDeliveryMode, BulkSettlementDeliveryMode } from '@/utils/Enumerations'
import PDFMerger from 'pdf-merger-js/browser'
import saveAs from 'file-saver'
import JSZip from 'jszip'
import { fileNameForString, getDateInfo, getFileNameForPeriod } from '../../utils/pdf-formatters/GenericSettlementsFunctions'
import { SettlementSubKeys } from '@/components/settlements/SettlementKeys.js'
export default {
  name: 'SettlementSheetPayeeSelector',

  components: {
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    deliveryMethod: {
      type: Object,
      required: true
    },
    emailScaleData: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    payees: [],
    allPayees: false,
    loading: false,
    settlementsByPayee: undefined,
    BulkSettlementDeliveryMode
  }),

  async created () {
    this.loading = true

    this.settlementsByPayee = this.currentSubModuleKey === SettlementSubKeys.AP.SettlementsByContract
      ? await this.fetchSettlementsByPayee()
      : this.currentSubModule

    const settlementPayees = this.emailScaleData
      ? this.settlementsByPayee?.payees?.filter(p => p.contracts.some(c => c.grossVolume > 0)) ?? []
      : this.settlementsByPayee?.payees ?? []

    const contactGroups = await this.fetchContactsForAccounts({
      accountIds: settlementPayees.map(p => p.payeeId),
      limitToSettlementRecipients: true
    })
    const contactGroupsByAccountId = Object.fromEntries(contactGroups.map(group => [group.accountId, group]))
    const isEmailDelivery = this.deliveryMethod.value === BulkSettlementDeliveryMode.Email.value

    this.payees = settlementPayees
      .filter(p => !isEmailDelivery || (contactGroupsByAccountId[p.payeeId]?.contacts?.length ?? 0) > 0)
      .map(p => {
        const deliveryMode = contactGroupsByAccountId[p.payeeId]?.settlementDeliveryMode
        return {
          accountId: p.payeeId,
          name: p.payeeName,
          selected: isEmailDelivery
            ? (deliveryMode === SettlementDeliveryMode.Email.value || deliveryMode === SettlementDeliveryMode.EmailAndPrint.value)
            : (deliveryMode === SettlementDeliveryMode.Print.value || deliveryMode === SettlementDeliveryMode.EmailAndPrint.value)
        }
      })

    this.loading = false
  },

  watch: {
    payees: {
      handler (val) {
        this.allPayees = val.every(p => p.selected)
      },
      deep: true
    }
  },

  computed: {
    ...mapGetters('settlements', ['currentSubModule', 'currentSubModuleKey', 'dateConfiguration']),
    buttonDisabled () {
      return this.payees.every(p => !p.selected)
    },
    downloadMethod () {
      switch (this.deliveryMethod.value) {
        case BulkSettlementDeliveryMode.SinglePdf.value:
          return this.downloadSinglePdf
        case BulkSettlementDeliveryMode.ZippedFolder.value:
          return this.downloadZippedPdf
        case BulkSettlementDeliveryMode.Email.value:
          return this.sendEmails
      }
      return this.zipped ? this.downloadZippedPdf : this.downloadSinglePdf
    },
    filename () {
      const dateInfo = getDateInfo(this.settlementsByPayee, this.dateConfiguration.exportBatch)
      return getFileNameForPeriod(dateInfo).replace(/\s/g, '')
    },
    btnText () {
      switch (this.deliveryMethod.value) {
        case BulkSettlementDeliveryMode.SinglePdf.value:
          return this.$t('downloadPDF')
        case BulkSettlementDeliveryMode.ZippedFolder.value:
          return this.$t('downloadZip')
        case BulkSettlementDeliveryMode.Email.value:
          return this.$t('sendEmails')
      }
      return 'exception'
    },
    noPayeesWithSettlementContacts () {
      return this.payees.length === 0 && this.deliveryMethod.value === BulkSettlementDeliveryMode.Email.value
    }
  },

  methods: {
    ...mapActions('settlements', ['fetchSubmoduleBlob', 'fetchSettlementsByPayee', 'createSettlementEmailRequest']),
    ...mapActions('account-contacts', ['fetchContactsForAccounts']),
    ...mapActions('scale-sheet', ['createScaleSheetEmailRequest']),
    allPayeeCheckboxToggled (event) {
      this.payees.forEach(p => { p.selected = event })
    },

    async downloadSinglePdf () {
      const merger = new PDFMerger()
      for (const payee of this.payees.filter(p => p.selected)) {
        const blob = await this.fetchSubmoduleBlob({ accountId: payee.accountId })
        await merger.add(blob)
      }
      const mergedPdf = await merger.saveAsBlob()
      saveAs(mergedPdf, fileNameForString(this.filename, 'pdf'))
      this.$emit('close', { refresh: false })
    },

    async downloadZippedPdf () {
      const zip = new JSZip()
      const folderName = fileNameForString(this.filename)
      const folder = zip.folder(folderName)
      for (const payee of this.payees.filter(p => p.selected)) {
        const blob = await this.fetchSubmoduleBlob({ accountId: payee.accountId })
        folder.file(`${fileNameForString(payee.name, 'pdf')}`, blob)
      }
      const downloadableZip = await zip.generateAsync({ type: 'blob' })
      saveAs(downloadableZip, fileNameForString(this.filename), 'zip')
      this.$emit('close', { refresh: false })
    },

    async sendEmails () {
      const requests = this.payees.filter(p => p.selected).map(p => ({
        accountId: p.accountId,
        accountContactId: null
      }))
      if (this.emailScaleData) {
        await Promise.all(requests.map(request => this.createScaleSheetEmailRequest(request)))
      } else {
        await Promise.all(requests.map(request => this.createSettlementEmailRequest(request)))
      }
      this.$emit('close', { refresh: true })
    }
  }
}
</script>
